import moment from "moment/min/moment-with-locales";
import { withMomentLocale } from "./eventFormat";

/**
 * Coupon helpers shared by the business coupon centre, the create form and the
 * student redeem centre.
 */

export type BusinessCoupon = {
  id: string;
  coupon_image?: string | null;
  coupon_name?: string | null;
  coupon_category?: string | null;
  original_price?: number | string | null;
  redeem_point?: number | string | null;
  issued_quantity?: number | string | null;
  infinity_quantity?: boolean | number | null;
  max_coupons?: number | string | null;
  redeem_times_per_user?: number | string | null;
  terms_conditions_en?: string | null;
  terms_conditions_zh_hant?: string | null;
  scan_by_qr_code?: boolean | number | null;
  offer_type?: string | null;
  active?: boolean | number | null;
  deleted?: boolean | number | null;
  expiry_date?: string | null;
  ctime?: string | null;
};

/** i18n key per `coupon_category` code stored in the DB. */
export const COUPON_CATEGORY_I18N_KEYS: Record<string, string> = {
  discount: "Discount Coupon",
  bogo: "Buy 1 Get 1 Free",
  freeItem: "Free Item",
  freeShipping: "Free Shipping",
  cashback: "Cashback",
  bundle: "Bundle / Package",
  timeSlotDeal: "Time-slot Deal",
  groupDeal: "Group Deal",
  reservationDiscount: "Reservation Discount",
  freeUpgrade: "Free Upgrade",
  addOn: "Add-on Deal",
  pointsMultiplier: "Points Multiplier",
  birthday: "Birthday Special",
  depositWaiver: "Deposit Waiver",
  firstTime: "First-time Deal",
};

export const COUPON_CATEGORY_CODES = Object.keys(COUPON_CATEGORY_I18N_KEYS);

/** `coupon_category` codes accepted by the create-coupon form. */
export type CouponCategory =
  | "discount"
  | "bogo"
  | "freeItem"
  | "freeShipping"
  | "cashback"
  | "bundle"
  | "timeSlotDeal"
  | "groupDeal"
  | "reservationDiscount"
  | "freeUpgrade"
  | "addOn"
  | "pointsMultiplier"
  | "birthday"
  | "depositWaiver"
  | "firstTime";

/** The DB stores booleans as `true | 0 | 1 | "0" | "1"` depending on the route. */
export const isTruthyFlag = (value?: boolean | number | string | null) =>
  value === true || value === 1 || value === "1";

/** A coupon can only be redeemed by QR when it was created with QR redemption on. */
export const isQrCoupon = (coupon?: BusinessCoupon | null) =>
  isTruthyFlag(coupon?.scan_by_qr_code);

/** Terms in the reader's language, falling back to the other language. */
export const getCouponTerms = (
  coupon: BusinessCoupon | null | undefined,
  isZh: boolean,
) => {
  const zh = String(coupon?.terms_conditions_zh_hant ?? "").trim();
  const en = String(coupon?.terms_conditions_en ?? "").trim();
  return isZh ? zh || en : en || zh;
};

/** `YYYY-MM-DD`, or an empty string when the coupon has no / invalid expiry. */
export const formatCouponDate = (
  value?: string | null,
  language?: string,
) => {
  if (!value) return "";
  withMomentLocale(language ?? "en");
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};
